import React, { useState } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import Icon from '../../../AppIcon';
import Button from '../../../ui/Button';

const PredictiveAnalytics = () => {
  const [selectedMetric, setSelectedMetric] = useState('matches');
  const [timeHorizon, setTimeHorizon] = useState('6months');

  const predictionData = {
    matches: [
      { month: 'Jan 2024', actual: 245, predicted: null, confidence: null },
      { month: 'Feb 2024', actual: 312, predicted: null, confidence: null },
      { month: 'Mar 2024', actual: 289, predicted: null, confidence: null },
      { month: 'Apr 2024', actual: 378, predicted: null, confidence: null },
      { month: 'May 2024', actual: 425, predicted: null, confidence: null },
      { month: 'Jun 2024', actual: 398, predicted: null, confidence: null },
      { month: 'Jul 2024', actual: null, predicted: 445, confidence: 0.87 },
      { month: 'Aug 2024', actual: null, predicted: 478, confidence: 0.82 },
      { month: 'Sep 2024', actual: null, predicted: 512, confidence: 0.78 },
      { month: 'Oct 2024', actual: null, predicted: 489, confidence: 0.74 },
      { month: 'Nov 2024', actual: null, predicted: 523, confidence: 0.69 },
      { month: 'Dec 2024', actual: null, predicted: 567, confidence: 0.65 }
    ],
    revenue: [
      { month: 'Jan 2024', actual: 380000, predicted: null, confidence: null },
      { month: 'Feb 2024', actual: 420000, predicted: null, confidence: null },
      { month: 'Mar 2024', actual: 395000, predicted: null, confidence: null },
      { month: 'Apr 2024', actual: 485000, predicted: null, confidence: null },
      { month: 'May 2024', actual: 520000, predicted: null, confidence: null },
      { month: 'Jun 2024', actual: 495000, predicted: null, confidence: null },
      { month: 'Jul 2024', actual: null, predicted: 545000, confidence: 0.89 },
      { month: 'Aug 2024', actual: null, predicted: 578000, confidence: 0.85 },
      { month: 'Sep 2024', actual: null, predicted: 612000, confidence: 0.81 },
      { month: 'Oct 2024', actual: null, predicted: 589000, confidence: 0.77 },
      { month: 'Nov 2024', actual: null, predicted: 634000, confidence: 0.72 },
      { month: 'Dec 2024', actual: null, predicted: 678000, confidence: 0.68 }
    ]
  };

  const metrics = [
    { id: 'matches', name: 'Successful Matches', icon: 'Target', unit: '' },
    { id: 'revenue', name: 'Revenue', icon: 'DollarSign', unit: '$' }
  ];

  const timeHorizons = [
    { value: '3months', label: '3 Months' },
    { value: '6months', label: '6 Months' },
    { value: '1year', label: '1 Year' }
  ];

  const insights = [
    {
      id: 1,
      type: 'trend',
      title: 'Growth Acceleration Expected',
      description: 'Model predicts 23% increase in match rate over next 6 months based on current trajectory and seasonal patterns.',
      confidence: 0.84,
      impact: 'high',
      icon: 'TrendingUp'
    },
    {
      id: 2,
      type: 'opportunity',
      title: 'Q4 Revenue Surge Likely',
      description: 'Historical data suggests Q4 typically sees 15-20% revenue boost. Current indicators align with this pattern.',
      confidence: 0.78,
      impact: 'medium',
      icon: 'DollarSign'
    },
    {
      id: 3,
      type: 'risk',
      title: 'Potential Conversion Dip',
      description: 'Market saturation indicators suggest possible 5-8% conversion rate decline in competitive sectors.',
      confidence: 0.71,
      impact: 'medium',
      icon: 'AlertTriangle'
    },
    {
      id: 4,
      type: 'recommendation',
      title: 'Expand Regional Focus',
      description: 'Asia-Pacific region shows 40% higher growth potential compared to saturated North American markets.',
      confidence: 0.82,
      impact: 'high',
      icon: 'Globe'
    }
  ];

  const currentData = predictionData?.[selectedMetric];
  const currentMetric = metrics?.find(m => m?.id === selectedMetric);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-popover-foreground mb-2">{label}</p>
          {data?.actual !== null && (
            <p className="text-sm text-primary">
              Actual: {currentMetric?.unit}{data?.actual?.toLocaleString()}
            </p>
          )}
          {data?.predicted !== null && (
            <>
              <p className="text-sm text-accent">
                Predicted: {currentMetric?.unit}{data?.predicted?.toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground">
                Confidence: {(data?.confidence * 100)?.toFixed(0)}%
              </p>
            </>
          )}
        </div>
      );
    }
    return null;
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      default: return 'text-muted-foreground';
    }
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case 'trend': return 'TrendingUp';
      case 'opportunity': return 'Zap';
      case 'risk': return 'AlertTriangle';
      case 'recommendation': return 'Lightbulb';
      default: return 'Info';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">
              Predictive Analytics
            </h3>
            <p className="text-sm text-muted-foreground">
              AI-powered forecasting and trend analysis for strategic planning
            </p>
          </div>

          <div className="flex items-center space-x-3 mt-4 sm:mt-0">
            <select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e?.target?.value)}
              className="px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {metrics?.map((metric) => (
                <option key={metric?.id} value={metric?.id}>
                  {metric?.name}
                </option>
              ))}
            </select>

            <select
              value={timeHorizon}
              onChange={(e) => setTimeHorizon(e?.target?.value)}
              className="px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {timeHorizons?.map((horizon) => (
                <option key={horizon?.value} value={horizon?.value}>
                  {horizon?.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Prediction Chart */}
        <div className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={currentData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="month" 
                stroke="var(--color-muted-foreground)"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${currentMetric?.unit}${value?.toLocaleString()}`}
              />
              <Tooltip content={<CustomTooltip />} />
              
              {/* Actual Data */}
              <Area
                type="monotone"
                dataKey="actual"
                stroke="var(--color-primary)"
                fill="var(--color-primary)"
                fillOpacity={0.3}
                strokeWidth={2}
              />
              
              {/* Predicted Data */}
              <Area
                type="monotone"
                dataKey="predicted"
                stroke="var(--color-accent)"
                fill="var(--color-accent)"
                fillOpacity={0.2}
                strokeWidth={2}
                strokeDasharray="5 5"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center space-x-6 mt-4 pt-4 border-t border-border">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-primary rounded-full"></div>
            <span className="text-sm text-muted-foreground">Actual Data</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-accent rounded-full opacity-60"></div>
            <span className="text-sm text-muted-foreground">Predicted Data</span>
          </div>
        </div>
      </div>
      {/* AI Insights */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h4 className="text-lg font-semibold text-foreground mb-1">
              AI-Generated Insights
            </h4>
            <p className="text-sm text-muted-foreground">
              Machine learning analysis of trends and recommendations
            </p>
          </div>
          
          <Button variant="outline" size="sm">
            <Icon name="RefreshCw" size={16} className="mr-2" />
            Refresh Analysis
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {insights?.map((insight) => (
            <div key={insight?.id} className="bg-muted/30 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name={getInsightIcon(insight?.type)} size={16} className="text-primary" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <h5 className="font-medium text-foreground">{insight?.title}</h5>
                    <span className={`text-xs font-medium ${getImpactColor(insight?.impact)}`}>
                      {insight?.impact?.toUpperCase()}
                    </span>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-3">
                    {insight?.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-full bg-muted rounded-full h-1.5 max-w-[80px]">
                        <div
                          className="bg-primary h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${insight?.confidence * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {(insight?.confidence * 100)?.toFixed(0)}% confidence
                      </span>
                    </div>
                    
                    <button className="text-xs text-primary hover:text-primary/80 transition-colors duration-200">
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Model Performance */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-lg font-semibold text-foreground mb-4">
          Model Performance & Accuracy
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="Target" size={24} className="text-success" />
            </div>
            <h5 className="font-semibold text-foreground mb-1">87.3%</h5>
            <p className="text-sm text-muted-foreground">Overall Accuracy</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="TrendingUp" size={24} className="text-primary" />
            </div>
            <h5 className="font-semibold text-foreground mb-1">92.1%</h5>
            <p className="text-sm text-muted-foreground">Trend Prediction</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="Clock" size={24} className="text-accent" />
            </div>
            <h5 className="font-semibold text-foreground mb-1">15 min</h5>
            <p className="text-sm text-muted-foreground">Update Frequency</p>
          </div>
        </div>
        
        <div className="mt-6 pt-6 border-t border-border text-center">
          <p className="text-xs text-muted-foreground">
            Model last trained: November 10, 2024 • Next update: November 17, 2024
          </p>
        </div>
      </div>
    </div>
  );
};

export default PredictiveAnalytics;